const AUTH_STORAGE_KEY = 'timeImprintAuth'
const USER_API_BASE = '/api/v1/users'

function readErrorMessage(payload, fallback) {
  if (!payload || typeof payload !== 'object') return fallback
  return payload.message || fallback
}

function announceUnauthenticated() {
  clearStoredAuth()
  window.dispatchEvent(new CustomEvent('timeimprint:unauthenticated'))
}

async function request(path, {
  method = 'POST',
  body,
  token,
  fallback = '请求失败，请稍后再试',
  suppressUnauthenticated = false,
} = {}) {
  const response = await fetch(`${USER_API_BASE}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  let payload = null
  try {
    payload = await response.json()
  } catch (error) {
    throw new Error(`HTTP ${response.status}`)
  }

  if (!response.ok || payload.code !== 'OK') {
    if (!suppressUnauthenticated && (response.status === 401 || payload.code === 'UNAUTHENTICATED')) {
      announceUnauthenticated()
    }
    throw new Error(readErrorMessage(payload, fallback))
  }
  return payload.data
}

export function loginWithPassword({ phoneNumber, password }) {
  return request('/login', {
    body: { phoneNumber, password },
    fallback: '登录失败，请稍后再试',
  })
}

export function verifyLoginAuthorization({ challenge, verificationString }) {
  return request('/login-authorization', {
    body: { challenge, verificationString },
    fallback: '授权验证失败，无法进入',
    suppressUnauthenticated: true,
  })
}

export function sendRegisterCode(phoneNumber) {
  return request('/sms', {
    body: { phoneNumber },
    fallback: '验证码发送失败，请稍后再试',
  })
}

export function registerWithPassword({ phoneNumber, password, code, nickname }) {
  return request('/register', {
    body: { phoneNumber, password, code, nickname },
    fallback: '注册失败，请稍后再试',
  })
}

export function getWechatLoginConfig(appType = 'web') {
  const query = new URLSearchParams({ appType })
  return request(`/wechat/config?${query}`, {
    method: 'GET',
    fallback: '微信登录暂不可用',
  })
}

/**
 * Converts the Task service's existing authorization URL into the fields
 * required by the official embedded WeChat QR component.
 */
export function parseWechatLoginAuthUrl(authUrl) {
  let url
  try {
    url = new URL(authUrl)
  } catch (error) {
    throw new Error('微信登录配置无效')
  }

  const config = {
    appid: url.searchParams.get('appid'),
    redirectUri: url.searchParams.get('redirect_uri'),
    scope: url.searchParams.get('scope') || 'snsapi_login',
    state: url.searchParams.get('state'),
  }
  if (!config.appid || !config.redirectUri || !config.state) {
    throw new Error('微信登录配置无效')
  }
  return config
}

/**
 * Builds the options owned by our page for the official WxLogin widget.
 * The href stylesheet is the supported way to style content inside its
 * cross-origin iframe; the host page must not crop or scale that iframe.
 */
export function buildWechatLoginEmbedConfig(authUrl, pageOrigin) {
  const config = parseWechatLoginAuthUrl(authUrl)
  return {
    appid: config.appid,
    scope: config.scope,
    redirect_uri: encodeURIComponent(config.redirectUri),
    state: config.state,
    style: 'black',
    href: new URL('/wechat-login.css', pageOrigin).href,
  }
}

export function loginWithWechatCode({ code, state, appType = 'web' }) {
  return request('/wechat/callback', {
    body: { code, state, appType },
    fallback: '微信登录失败，请稍后再试',
  })
}

export function bindWechatPhone({ token, phoneNumber, code, password }) {
  return request('/wechat/bind-phone', {
    token,
    body: { phoneNumber, code, password },
    fallback: '手机号关联失败，请稍后再试',
  })
}

export function logoutUser(token) {
  return request('/logout', {
    token,
    fallback: '退出登录失败',
  })
}

export function loadStoredAuth() {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    return null
  }
}

export function saveStoredAuth(user) {
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user))
}

export function clearStoredAuth() {
  window.localStorage.removeItem(AUTH_STORAGE_KEY)
}

export function hasAuthenticatedSession(user) {
  return Boolean(user?.token)
}

export function needsPhoneBinding(user) {
  return Boolean(user?.token && !user.phoneNumber)
}

export function userPageCount(total, pageSize) {
  return Math.max(1, Math.ceil(Number(total || 0) / Number(pageSize || 20)))
}

export function getRegisteredUsers(token, { page = 1, pageSize = 20, keyword = '' } = {}) {
  const query = new URLSearchParams({ page, pageSize })
  if (keyword) query.set('keyword', keyword)
  return request(`?${query}`, {
    method: 'GET',
    token,
    fallback: '用户信息加载失败',
  })
}
